import { useId, type SVGProps } from 'react';

/**
 * Instagram brand icon — replicated verbatim from the Figma design system
 * ("Social icon" page, node 5422:4230). Full-colour gradient mark; `color`/
 * `fill` props do not tint it. Single source of truth for the IG mark.
 * Gradient ids are scoped with useId so multiple instances don't collide.
 */
export function InstagramIcon({
  size = 24,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { size?: number }) {
  const uid = useId();
  const lin = `${uid}-lin`;
  const rad = `${uid}-rad`;
  const ring =
    'M7.41474 14C7.41474 10.349 10.363 7.38936 14 7.38936C17.637 7.38936 20.5853 10.349 20.5853 14C20.5853 17.651 17.637 20.6106 14 20.6106C10.363 20.6106 7.41474 17.651 7.41474 14ZM14 18.2911C11.6392 18.2911 9.72535 16.3699 9.72535 14C9.72535 11.6301 11.6392 9.70888 14 9.70888C16.3608 9.70888 18.2746 11.6301 18.2746 14C18.2746 16.3699 16.3608 18.2911 14 18.2911Z';
  const dot =
    'M20.8454 8.67296C21.6953 8.67296 22.3843 7.98136 22.3843 7.12818C22.3843 6.275 21.6953 5.58335 20.8454 5.58335C19.9956 5.58335 19.3066 6.275 19.3066 7.12818C19.3066 7.98136 19.9956 8.67296 20.8454 8.67296Z';
  const frame =
    'M14 1.12663C10.5172 1.12663 10.0805 1.14145 8.71271 1.2041C7.34775 1.2666 6.41553 1.48424 5.59984 1.80249C4.75655 2.13143 4.04139 2.57161 3.32842 3.28728C2.6155 4.003 2.17701 4.72092 1.84933 5.56746C1.5323 6.3863 1.3155 7.32211 1.25324 8.69233C1.19083 10.0654 1.17607 10.5038 1.17607 14C1.17607 17.4962 1.19083 17.9346 1.25324 19.3077C1.3155 20.6779 1.5323 21.6137 1.84933 22.4325C2.17701 23.2791 2.6155 23.997 3.32842 24.7127C4.04139 25.4284 4.75655 25.8686 5.59984 26.1976C6.41553 26.5158 7.34775 26.7334 8.71271 26.7959C10.0805 26.8585 10.5172 26.8734 14 26.8734C17.4828 26.8734 17.9195 26.8585 19.2873 26.7959C20.6522 26.7334 21.5845 26.5158 22.4002 26.1976C23.2434 25.8686 23.9586 25.4284 24.6716 24.7127C25.3845 23.997 25.823 23.2791 26.1507 22.4325C26.4677 21.6137 26.6845 20.6779 26.7468 19.3077C26.8092 17.9346 26.8239 17.4962 26.8239 14C26.8239 10.5038 26.8092 10.0654 26.7468 8.69233C26.6845 7.32211 26.4677 6.3863 26.1507 5.56746C25.823 4.72092 25.3845 4.003 24.6716 3.28728C23.9586 2.57161 23.2434 2.13143 22.4002 1.80249C21.5845 1.48424 20.6522 1.2666 19.2873 1.2041C17.9195 1.14145 17.4828 1.12663 14 1.12663ZM14 3.44615C17.4241 3.44615 17.8297 3.45929 19.182 3.52122C20.4323 3.57845 21.1113 3.78817 21.5632 3.96447C22.1618 4.198 22.589 4.47696 23.0377 4.92747C23.4865 5.37792 23.7644 5.80676 23.997 6.40766C24.1726 6.86128 24.3815 7.54291 24.4385 8.79806C24.5002 10.1555 24.5133 10.5627 24.5133 14C24.5133 17.4373 24.5002 17.8445 24.4385 19.2019C24.3815 20.4571 24.1726 21.1387 23.997 21.5923C23.7644 22.1932 23.4865 22.6221 23.0377 23.0725C22.589 23.523 22.1618 23.802 21.5632 24.0355C21.1113 24.2118 20.4323 24.4215 19.182 24.4788C17.8299 24.5407 17.4244 24.5538 14 24.5538C10.5756 24.5538 10.1701 24.5407 8.81803 24.4788C7.56771 24.4215 6.8887 24.2118 6.43682 24.0355C5.83823 23.802 5.41104 23.523 4.96231 23.0725C4.51359 22.6221 4.23565 22.1932 4.00301 21.5923C3.82739 21.1387 3.61848 20.4571 3.56147 19.2019C3.49977 17.8445 3.48669 17.4373 3.48669 14C3.48669 10.5627 3.49977 10.1555 3.56147 8.79806C3.61848 7.54291 3.82739 6.86128 4.00301 6.40766C4.23565 5.80676 4.51354 5.37792 4.96231 4.92747C5.41104 4.47696 5.83823 4.198 6.43682 3.96447C6.8887 3.78817 7.56771 3.57845 8.81803 3.52122C10.1703 3.45929 10.5759 3.44615 14 3.44615Z';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      {...props}
    >
      <path fillRule="evenodd" clipRule="evenodd" d={ring} fill={`url(#${lin})`} />
      <path fillRule="evenodd" clipRule="evenodd" d={ring} fill={`url(#${rad})`} />
      <path d={dot} fill={`url(#${lin})`} />
      <path d={dot} fill={`url(#${rad})`} />
      <path fillRule="evenodd" clipRule="evenodd" d={frame} fill={`url(#${lin})`} />
      <path fillRule="evenodd" clipRule="evenodd" d={frame} fill={`url(#${rad})`} />
      <defs>
        <linearGradient id={lin} x1="2.67254" y1="2.48021" x2="10.8176" y2="33.8654" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4E60D3" />
          <stop offset="0.142763" stopColor="#913BAF" />
          <stop offset="0.761458" stopColor="#D52D88" />
          <stop offset="1" stopColor="#F26D4F" />
        </linearGradient>
        <radialGradient
          id={rad}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(20.2784 12.7994 -9.17595 14.65 8.94079 26.8734)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FED276" />
          <stop offset="0.17024" stopColor="#FDBD61" stopOpacity="0.975006" />
          <stop offset="0.454081" stopColor="#F6804D" />
          <stop offset="1" stopColor="#E83D5C" stopOpacity="0.01" />
        </radialGradient>
      </defs>
    </svg>
  );
}
